import React from 'react';
import { TextInput, TextInputProps } from 'react-admin';
import { TextField, InputAdornment } from '@mui/material';
import { Schedule as ScheduleIcon } from '@mui/icons-material';

interface HoursMinutesInputProps extends Omit<TextInputProps, 'parse' | 'format'> {
  source: string;
  label?: string;
  fullWidth?: boolean;
}

// Utility function to format decimal hours to HH:MM format
export const formatHoursToHHMM = (decimalHours?: number | null): string => {
  if (!decimalHours || decimalHours <= 0) {
    return '00:00';
  }
  
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

// Utility function to parse HH:MM format to decimal hours
const parseHHMMToHours = (timeString?: string): number | null => {
  if (!timeString) return null;
  
  const timePattern = /^(\d{1,2}):(\d{2})$/;
  const match = timeString.match(timePattern);
  
  if (!match) return null;
  
  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  
  if (minutes >= 60) return null;
  
  return hours + (minutes / 60);
};

export const HoursMinutesInput: React.FC<HoursMinutesInputProps> = (props) => {
  const { source, label, fullWidth, ...rest } = props;

  return (
    <TextInput
      source={source}
      label={label}
      fullWidth={fullWidth}
      parse={(value) => parseHHMMToHours(value)}
      format={(value) => formatHoursToHHMM(value)}
      placeholder="HH:MM"
      helperText="Enter time in HH:MM format (e.g., 02:30 for 2 hours 30 minutes)"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <ScheduleIcon color="action" />
          </InputAdornment>
        ),
      }}
      {...rest}
    />
  );
};

export default HoursMinutesInput;